import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Send, CheckCircle, Trash2, Printer, MailCheck, Ban, RotateCcw, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { InvoicePaper } from "@/components/InvoicePaper";
import { StatusBadge } from "@/components/StatusBadge";
import { MarkPaidDialog } from "@/components/MarkPaidDialog";
import { VoidDialog } from "@/components/VoidDialog";
import { useLogoUrl } from "@/components/LogoUploader";
import { api, errMsg, fmtDate } from "@/lib/api";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState("");
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const logoUrl = useLogoUrl(!!inv?.company?.logo_path, 0, `/invoices/${id}/logo`);

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((e) => { toast.error(errMsg(e)); navigate("/reports"); });
  }, [id, navigate]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e), { duration: 9000 }); } finally { setBusy(""); }
  };

  const download = () => run("pdf", async () => {
    const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: `${inv.invoice_number}.pdf` });
    a.click();
    URL.revokeObjectURL(url);
  });

  const send = () => run("send", async () => {
    const r = await api.post(`/invoices/${id}/send`);
    setInv(r.data);
    toast.success(`Emailed to ${r.data.sent_to}`);
  });

  const setStatus = (status) => run(status, async () => {
    const r = await api.patch(`/invoices/${id}/status`, { status });
    setInv(r.data);
    toast.success(`Marked as ${status}`);
  });

  const sendReceipt = () => run("receipt", async () => {
    const r = await api.post(`/invoices/${id}/receipt`);
    setInv(r.data);
    toast.success(`Receipt emailed to ${inv.client.email}`);
  });

  const remove = () => {
    if (!window.confirm("Delete this invoice?")) return;
    run("delete", async () => { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted"); navigate(inv.status === "draft" ? "/drafts" : "/reports"); });
  };

  if (!inv) return <div className="text-sm text-muted-foreground">Loading invoice...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to={inv.status === "draft" ? "/drafts" : "/reports"} data-testid="invoice-back-link" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-secondary"><ArrowLeft className="h-4 w-4" /></Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-extrabold tracking-tight font-mono">{inv.invoice_number}</h1>
              <StatusBadge status={inv.status} id={inv.id} />
            </div>
            {inv.sent_to && <p className="text-xs text-muted-foreground" data-testid="invoice-sent-info">Sent to {inv.sent_to}</p>}
            {inv.status === "paid" && <p className="text-xs text-green-600 font-medium" data-testid="invoice-paid-info">Paid {inv.paid_date ? fmtDate(inv.paid_date) : ""}{inv.receipt_sent_at ? " · receipt emailed" : ""}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {inv.status === "draft" && <Button asChild variant="outline" className="border-emerald-600 text-emerald-700 dark:text-emerald-400"><Link to={`/drafts/${id}`} data-testid="invoice-edit-draft-button"><Pencil className="h-4 w-4 mr-2" />Edit Draft</Link></Button>}
          <Button data-testid="invoice-download-pdf-button" variant="outline" onClick={download} disabled={!!busy}><Download className="h-4 w-4 mr-2" />{busy === "pdf" ? "..." : "PDF"}</Button>
          <Button data-testid="invoice-print-button" variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" />Print</Button>
          <Button data-testid="invoice-send-email-button" onClick={send} disabled={!!busy || !inv.client?.email || inv.status === "void"} className="zap-btn rounded-lg">
            <Send className="h-4 w-4 mr-2" />{busy === "send" ? "Sending..." : inv.status === "draft" ? "Email Invoice" : "Resend"}
          </Button>
          {inv.status === "void" ? (
            <Button data-testid="invoice-restore-button" variant="outline" onClick={() => setStatus(inv.sent_at ? "sent" : "draft")} disabled={!!busy} className="border-emerald-600 text-emerald-700 dark:text-emerald-400"><RotateCcw className="h-4 w-4 mr-2" />Restore</Button>
          ) : inv.status !== "paid" ? (
            <Button data-testid="invoice-mark-paid-button" onClick={() => setPaying(true)} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle className="h-4 w-4 mr-2" />Mark Paid</Button>
          ) : (
            <>
              <Button data-testid="invoice-send-receipt-button" variant="outline" onClick={sendReceipt} disabled={!!busy || !inv.client?.email} className="border-emerald-600 text-emerald-700 dark:text-emerald-400">
                <MailCheck className="h-4 w-4 mr-2" />{busy === "receipt" ? "Sending..." : inv.receipt_sent_at ? "Resend Receipt" : "Email Receipt"}
              </Button>
              <Button data-testid="invoice-mark-unpaid-button" variant="outline" onClick={() => setStatus("sent")} disabled={!!busy}>Mark Unpaid</Button>
            </>
          )}
          {inv.status !== "void" && <Button data-testid="invoice-void-button" variant="ghost" onClick={() => setVoiding(true)} disabled={!!busy} className="text-red-600 hover:text-red-700"><Ban className="h-4 w-4 mr-2" />Void</Button>}
          <Button data-testid="invoice-delete-button" variant="ghost" onClick={remove} disabled={!!busy} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {inv.status === "void" && <p className="text-xs text-red-600 font-medium" data-testid="invoice-void-info">Voided{inv.void_reason ? ` — ${inv.void_reason}` : ""}. Excluded from all totals; restore to make it active again.</p>}
      {!inv.client?.email && <p className="text-xs text-amber-600 font-medium">Client has no email on file — download the PDF to send it manually.</p>}
      <InvoicePaper inv={inv} logoUrl={logoUrl} />
      {voiding && <VoidDialog invoice={inv} onClose={() => setVoiding(false)} onDone={setInv} />}
      {paying && <MarkPaidDialog invoice={inv} onClose={() => setPaying(false)} onDone={setInv} />}
    </div>
  );
}
